import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageSEO from "@/components/feature/PageSEO";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://chirunnexa.com";

/* ─── Data ─────────────────────────────────────────────── */
const engineBlocks = [
  {
    model: "Renault / Lada K4M",
    oemPart: "7701473181 / 8200267138",
    application: "Renault Duster, Logan, Megane II, Sandero; Lada Largus, Vesta",
    material: "Gray Cast Iron (GG25)",
    bore: "79.5 × 80.5 mm",
    displacement: "1598 cc (1.6L)",
    valvetrain: "DOHC 16V",
    markets: ["Russia", "Middle East", "Europe"],
    badge: "HIGH DEMAND",
    badgeColor: "#D97706",
  },
  {
    model: "Mercedes-Benz OM651",
    oemPart: "A6510100005 / A6510100105",
    application: "C-Class W204/W205, E-Class W212, GLK X204, Sprinter 906",
    material: "Compacted Graphite Iron (CGI)",
    bore: "83.0 × 99.0 mm",
    displacement: "2143 cc (2.1L)",
    valvetrain: "DOHC 16V CDI",
    markets: ["Europe", "Middle East"],
    badge: "PREMIUM",
    badgeColor: "#1a1a1a",
  },
  {
    model: "Mitsubishi 4D56",
    oemPart: "MD185926 / MN980252",
    application: "L200 Triton, Pajero Sport, Delica, Galant, Eclipse",
    material: "Gray Cast Iron (GG30)",
    bore: "91.1 × 95.0 mm",
    displacement: "2477 cc (2.5L)",
    valvetrain: "SOHC 8V Diesel",
    markets: ["Middle East", "Russia"],
    badge: "POPULAR",
    badgeColor: "#059669",
  },
  {
    model: "Cummins ISF 2.8",
    oemPart: "5289698 / 5289699",
    application: "GAZ Gazelle Next, UAZ Patriot, Foton Aumark, LCV Commercial",
    material: "Ductile Iron (GGG50)",
    bore: "94.0 × 100.0 mm",
    displacement: "2776 cc (2.8L)",
    valvetrain: "DOHC 16V Common Rail",
    markets: ["Russia", "Middle East"],
    badge: "COMMERCIAL",
    badgeColor: "#7C3AED",
  },
  {
    model: "ZMZ-405",
    oemPart: "405.1002010-10 / 405.1002010-30",
    application: "GAZ-3110 Volga, GAZ-2705 Gazelle, UAZ-3163 Patriot",
    material: "Gray Cast Iron (GG25)",
    bore: "92.0 × 92.0 mm",
    displacement: "2445 cc (2.4L)",
    valvetrain: "DOHC 16V",
    markets: ["Russia"],
    badge: "CIS MARKET",
    badgeColor: "#DC2626",
  },
];

const brands = [
  {
    name: "Mercedes-Benz",
    logoUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/90/Mercedes-Logo.svg/120px-Mercedes-Logo.svg.png",
    models: "OM651 · OM642 · M271 · M272",
  },
  {
    name: "BMW",
    logoUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/BMW.svg/120px-BMW.svg.png",
    models: "N47 · N57 · B47 · B57",
  },
  {
    name: "Audi",
    logoUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/9/92/Audi-Logo_2016.svg/200px-Audi-Logo_2016.svg.png",
    models: "EA189 · EA288 · EA897",
  },
  {
    name: "Renault",
    logoUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/4/49/Renault_2021_Text.svg/200px-Renault_2021_Text.svg.png",
    models: "K4M · K9K · M9R · H4M",
  },
  {
    name: "Mitsubishi",
    logoUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/8/8e/Mitsubishi_logo.svg/120px-Mitsubishi_logo.svg.png",
    models: "4D56 · 4N15 · 6G72 · 4G63",
  },
  {
    name: "Cummins",
    logoUrl: "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3e/Cummins_logo.svg/200px-Cummins_logo.svg.png",
    models: "ISF 2.8 · ISF 3.8 · ISBe · QSB",
  },
];

const marketColors: Record<string, { bg: string; text: string }> = {
  Russia: { bg: "rgba(220,38,38,0.08)", text: "#DC2626" },
  "Middle East": { bg: "rgba(217,119,6,0.1)", text: "#D97706" },
  Europe: { bg: "rgba(30,64,175,0.08)", text: "#1D4ED8" },
};

const catalogJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Engine Block Catalog | OEM Bare Block Supplier China | Chirunnexa",
    "url": `${SITE_URL}/engine-block-catalog`,
    "description": "Premium aftermarket bare engine blocks for global B2B supply. Renault K4M, Mercedes OM651, Mitsubishi 4D56, Cummins ISF 2.8, ZMZ-405. Export to Russia, Middle East, Europe. ISO 9001:2015 certified.",
    "inLanguage": "en",
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
        { "@type": "ListItem", "position": 2, "name": "Engine Block Catalog", "item": `${SITE_URL}/engine-block-catalog` },
      ],
    },
    "isPartOf": {
      "@type": "WebSite",
      "name": "Chirunnexa",
      "url": `${SITE_URL}/`,
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Engine Block Catalog — OEM Bare Block Supplier China",
    "description": "Premium aftermarket bare engine blocks for global B2B supply from Guangzhou. Renault K4M, Mercedes-Benz OM651, Mitsubishi 4D56, Cummins ISF 2.8, ZMZ-405. ISO 9001:2015 certified. Export to Russia, Middle East, Europe.",
    "url": `${SITE_URL}/engine-block-catalog`,
    "numberOfItems": 5,
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "Product",
          "name": "Renault / Lada K4M Bare Engine Block",
          "description": "Gray Cast Iron (GG25) bare engine block for Renault Duster, Logan, Megane II, Sandero; Lada Largus, Vesta. OEM Part: 7701473181 / 8200267138. Bore 79.5×80.5mm, 1598cc DOHC 16V.",
          "brand": { "@type": "Brand", "name": "Renault OEM Replacement" },
          "manufacturer": { "@type": "Organization", "name": "Chirunnexa", "url": `${SITE_URL}/` },
          "offers": { "@type": "Offer", "availability": "https://schema.org/InStock", "priceCurrency": "USD", "seller": { "@type": "Organization", "name": "Chirunnexa" } },
          "additionalProperty": [
            { "@type": "PropertyValue", "name": "OEM Part Number", "value": "7701473181 / 8200267138" },
            { "@type": "PropertyValue", "name": "Material", "value": "Gray Cast Iron GG25" },
            { "@type": "PropertyValue", "name": "Displacement", "value": "1598cc (1.6L)" },
            { "@type": "PropertyValue", "name": "Valvetrain", "value": "DOHC 16V" },
          ],
        },
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "Product",
          "name": "Mercedes-Benz OM651 Bare Engine Block",
          "description": "Compacted Graphite Iron (CGI) bare engine block for C-Class W204/W205, E-Class W212, GLK X204, Sprinter 906. OEM Part: A6510100005 / A6510100105. Bore 83.0×99.0mm, 2143cc DOHC 16V CDI.",
          "brand": { "@type": "Brand", "name": "Mercedes-Benz OEM Replacement" },
          "manufacturer": { "@type": "Organization", "name": "Chirunnexa", "url": `${SITE_URL}/` },
          "offers": { "@type": "Offer", "availability": "https://schema.org/InStock", "priceCurrency": "USD", "seller": { "@type": "Organization", "name": "Chirunnexa" } },
        },
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": {
          "@type": "Product",
          "name": "Mitsubishi 4D56 Bare Engine Block",
          "description": "Gray Cast Iron (GG30) bare engine block for L200 Triton, Pajero Sport, Delica, Galant, Eclipse. OEM Part: MD185926 / MN980252. Bore 91.1×95.0mm, 2477cc SOHC 8V Diesel.",
          "brand": { "@type": "Brand", "name": "Mitsubishi OEM Replacement" },
          "manufacturer": { "@type": "Organization", "name": "Chirunnexa", "url": `${SITE_URL}/` },
          "offers": { "@type": "Offer", "availability": "https://schema.org/InStock", "priceCurrency": "USD", "seller": { "@type": "Organization", "name": "Chirunnexa" } },
        },
      },
      {
        "@type": "ListItem",
        "position": 4,
        "item": {
          "@type": "Product",
          "name": "Cummins ISF 2.8 Bare Engine Block",
          "description": "Ductile Iron (GGG50) bare engine block for GAZ Gazelle Next, UAZ Patriot, Foton Aumark, LCV Commercial. OEM Part: 5289698 / 5289699. Bore 94.0×100.0mm, 2776cc DOHC 16V Common Rail.",
          "brand": { "@type": "Brand", "name": "Cummins OEM Replacement" },
          "manufacturer": { "@type": "Organization", "name": "Chirunnexa", "url": `${SITE_URL}/` },
          "offers": { "@type": "Offer", "availability": "https://schema.org/InStock", "priceCurrency": "USD", "seller": { "@type": "Organization", "name": "Chirunnexa" } },
        },
      },
      {
        "@type": "ListItem",
        "position": 5,
        "item": {
          "@type": "Product",
          "name": "ZMZ-405 Bare Engine Block",
          "description": "Gray Cast Iron (GG25) bare engine block for GAZ-3110 Volga, GAZ-2705 Gazelle, UAZ-3163 Patriot. OEM Part: 405.1002010-10 / 405.1002010-30. Bore 92.0×92.0mm, 2445cc DOHC 16V.",
          "brand": { "@type": "Brand", "name": "ZMZ OEM Replacement" },
          "manufacturer": { "@type": "Organization", "name": "Chirunnexa", "url": `${SITE_URL}/` },
          "offers": { "@type": "Offer", "availability": "https://schema.org/InStock", "priceCurrency": "USD", "seller": { "@type": "Organization", "name": "Chirunnexa" } },
        },
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Bare Engine Block Supply & Reverse Engineering — China Export",
    "description": "B2B supply of premium aftermarket bare engine blocks from Guangzhou. Reverse engineering from physical samples — no OEM blueprints needed. 3D scanning, CMM inspection, ISO 9001:2015 certified. FCL/LCL export to Russia, Middle East, Europe.",
    "url": `${SITE_URL}/engine-block-catalog`,
    "provider": {
      "@type": "Organization",
      "name": "Chirunnexa",
      "url": `${SITE_URL}/`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Guangzhou",
        "addressRegion": "Guangdong",
        "addressCountry": "CN",
      },
    },
    "serviceType": "Engine Block Manufacturing & Export",
    "areaServed": ["Global", "RU", "AE", "DE", "PL", "KZ"],
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Engine Block Catalog Services",
      "itemListElement": [
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Renault K4M Bare Block Supply" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Mercedes OM651 Bare Block Supply" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Reverse Engineering from Physical Samples" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "CMM Inspection & Quality Certification" } },
        { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "FCL/LCL Export via Guangzhou & Ningbo Ports" } },
      ],
    },
  },
];

/* ─── Contact Form ──────────────────────────────────────── */
function InquiryForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) return;
    setSubmitting(true);
    const data = new URLSearchParams();
    new FormData(form).forEach((v, k) => data.append(k, v as string));
    try {
      await fetch("https://readdy.ai/api/form/d7d21bs5q56ra7tufe1g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
    } finally {
      setSubmitted(true);
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-14 h-14 flex items-center justify-center rounded-full mb-4" style={{ background: "rgba(217,119,6,0.1)" }}>
          <i className="ri-check-line text-2xl" style={{ color: "#D97706" }} />
        </div>
        <h3 className="font-black text-lg mb-2" style={{ color: "#1a1a1a" }}>Inquiry Received</h3>
        <p className="text-sm font-light" style={{ color: "rgba(30,20,5,0.55)" }}>
          Our engineering team will respond within 24 business hours.
        </p>
      </div>
    );
  }

  return (
    <form data-readdy-form id="engine-block-catalog-inquiry" onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[
          { name: "full_name", label: "Full Name *", placeholder: "John Smith", type: "text", required: true },
          { name: "company_name", label: "Company *", placeholder: "Your Company Ltd.", type: "text", required: true },
        ].map((f) => (
          <div key={f.name} className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold" style={{ color: "rgba(30,20,5,0.5)" }}>{f.label}</label>
            <input
              type={f.type} name={f.name} required={f.required} placeholder={f.placeholder}
              className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
              style={{ background: "rgba(255,248,235,0.6)", border: "1.5px solid rgba(217,119,6,0.2)", color: "#1a1a1a", fontSize: "14px" }}
              onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.6)")}
              onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.2)")}
            />
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold" style={{ color: "rgba(30,20,5,0.5)" }}>Email Address *</label>
        <input
          type="email" name="email" required placeholder="john@company.com"
          className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
          style={{ background: "rgba(255,248,235,0.6)", border: "1.5px solid rgba(217,119,6,0.2)", color: "#1a1a1a", fontSize: "14px" }}
          onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.6)")}
          onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.2)")}
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold" style={{ color: "rgba(30,20,5,0.5)" }}>Engine Model</label>
          <input
            type="text" name="engine_model" placeholder="e.g. K4M, OM651, 4D56"
            className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
            style={{ background: "rgba(255,248,235,0.6)", border: "1.5px solid rgba(217,119,6,0.2)", color: "#1a1a1a", fontSize: "14px" }}
            onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.6)")}
            onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.2)")}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-semibold" style={{ color: "rgba(30,20,5,0.5)" }}>Quantity Required</label>
          <input
            type="text" name="quantity" placeholder="e.g. 20 pcs / month"
            className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200"
            style={{ background: "rgba(255,248,235,0.6)", border: "1.5px solid rgba(217,119,6,0.2)", color: "#1a1a1a", fontSize: "14px" }}
            onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.6)")}
            onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.2)")}
          />
        </div>
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold flex items-center justify-between" style={{ color: "rgba(30,20,5,0.5)" }}>
          <span>Additional Requirements</span>
          <span style={{ color: charCount > 500 ? "#ef4444" : "rgba(30,20,5,0.35)", fontSize: "11px" }}>{charCount}/500</span>
        </label>
        <textarea
          name="requirements" rows={3} maxLength={500}
          placeholder="OEM part numbers, destination port, special specs, or reverse engineering needs..."
          className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200 resize-none"
          style={{ background: "rgba(255,248,235,0.6)", border: "1.5px solid rgba(217,119,6,0.2)", color: "#1a1a1a", fontSize: "14px" }}
          onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.6)")}
          onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(217,119,6,0.2)")}
          onChange={(e) => setCharCount(e.target.value.length)}
        />
      </div>
      <button
        type="submit" disabled={submitting || charCount > 500}
        className="w-full flex items-center justify-center gap-2 py-4 rounded-xl font-bold tracking-widest text-sm text-white transition-all duration-200 hover:brightness-110 active:scale-95 cursor-pointer whitespace-nowrap disabled:opacity-60"
        style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", letterSpacing: "0.13em" }}
      >
        {submitting ? <><i className="ri-loader-4-line animate-spin" />Sending...</> : <><i className="ri-mail-send-line" />Send Inquiry</>}
      </button>
      <p className="text-xs text-center" style={{ color: "rgba(30,20,5,0.35)" }}>Response within 24 business hours · No commitment required</p>
    </form>
  );
}

/* ─── Page ──────────────────────────────────────────────── */
export default function EngineBlockCatalogPage() {
  const navigate = useNavigate();
  const [activeMarket, setActiveMarket] = useState<string>("all");

  const filtered = activeMarket === "all"
    ? engineBlocks
    : engineBlocks.filter((b) => b.markets.includes(activeMarket));

  return (
    <div style={{ background: "#ffffff", minHeight: "100vh" }}>
      <PageSEO
        title="Engine Block Catalog | OEM Bare Block Supplier China | Chirunnexa"
        description="Premium aftermarket bare engine blocks for global B2B supply. Renault K4M, Mercedes OM651, Mitsubishi 4D56, Cummins ISF 2.8, ZMZ-405. Export to Russia, Middle East, Europe. ISO 9001:2015 certified. B2B quote within 24h."
        keywords="engine block catalog, bare engine block supplier China, Renault K4M block, Mercedes OM651 block, Mitsubishi 4D56 block, Cummins ISF 2.8 block, ZMZ-405 block, aftermarket engine block wholesale"
        canonical="/engine-block-catalog"
        jsonLd={catalogJsonLd}
      />

      {/* ── Top Nav ── */}
      <nav
        className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 h-16"
        style={{ background: "rgba(255,255,255,0.97)", borderBottom: "1px solid rgba(217,119,6,0.12)", backdropFilter: "blur(16px)" }}
      >
        <Link to="/" className="flex items-center gap-3 cursor-pointer" style={{ textDecoration: "none" }}>
          <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)" }}>
            <i className="ri-settings-3-line text-sm text-white" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-black tracking-widest" style={{ fontSize: "13px", letterSpacing: "0.18em", color: "#1a1a1a" }}>CHIRUNNEXA</span>
            <span className="font-light" style={{ fontSize: "8px", letterSpacing: "0.2em", color: "rgba(30,20,5,0.4)", marginTop: "2px" }}>B2B AUTO PARTS PLATFORM</span>
          </div>
        </Link>
        <div className="hidden md:flex items-center gap-6">
          {[
            { label: "Engine Blocks", href: "#eb-table" },
            { label: "Brands", href: "#eb-brands" },
            { label: "Get Quote", href: "#eb-cta" },
          ].map((l) => (
            <a key={l.label} href={l.href}
              className="text-xs font-semibold tracking-widest uppercase transition-colors duration-200 whitespace-nowrap"
              style={{ color: "rgba(30,20,5,0.5)", letterSpacing: "0.14em", textDecoration: "none" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#D97706")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(30,20,5,0.5)")}
            >{l.label}</a>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")}
            className="hidden md:flex items-center gap-2 text-xs font-medium tracking-widest cursor-pointer transition-colors duration-200 whitespace-nowrap"
            style={{ color: "rgba(30,20,5,0.45)", letterSpacing: "0.12em", background: "none", border: "none" }}
            onMouseEnter={(e) => (e.currentTarget.style.color = "#D97706")}
            onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(30,20,5,0.45)")}
          >
            <i className="ri-arrow-left-line" /> Main Site
          </button>
          <a href="#eb-cta"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-bold tracking-widest text-white transition-all hover:brightness-110 cursor-pointer whitespace-nowrap"
            style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", letterSpacing: "0.12em", textDecoration: "none" }}
          >
            <i className="ri-mail-send-line" /> Get Quote
          </a>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative pt-16 overflow-hidden" style={{ minHeight: "520px" }}>
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/de14e3dd0f742bd058d88489e2e70739.jpg"
            alt="Engine block catalog premium aftermarket bare blocks manufacturing"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(255,251,245,0.97) 45%, rgba(255,251,245,0.7) 75%, rgba(255,251,245,0.3) 100%)" }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 flex flex-col justify-center" style={{ minHeight: "520px", paddingTop: "60px", paddingBottom: "60px" }}>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 self-start"
            style={{ background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.25)" }}>
            <i className="ri-database-2-line text-xs" style={{ color: "#D97706" }} />
            <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: "#D97706", letterSpacing: "0.2em" }}>
              Engine Block Catalog
            </span>
          </div>
          <h1 className="font-black leading-none mb-5" style={{ fontSize: "clamp(2.4rem, 5vw, 4.2rem)", color: "#1a1a1a", maxWidth: "700px" }}>
            Bare Engine Blocks
            <br />
            <span style={{ color: "#D97706" }}>Built for Extremes.</span>
          </h1>
          <p className="text-base font-light leading-relaxed mb-8" style={{ color: "rgba(30,20,5,0.6)", maxWidth: "560px", lineHeight: "1.85" }}>
            Engineered for extreme environments — supplying{" "}
            <strong style={{ color: "#1a1a1a" }}>premium aftermarket bare blocks</strong> for
            Europe, Russia, and the Middle East. From reverse engineering to mass production,
            every block is cast, machined, and CMM-inspected in-house.
          </p>
          <div className="flex flex-wrap gap-6">
            {[
              { icon: "ri-shield-check-line", label: "ISO 9001:2015 Certified" },
              { icon: "ri-scan-2-line", label: "Reverse Engineering Available" },
              { icon: "ri-ship-line", label: "FCL / LCL Export Ready" },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <i className={`${item.icon} text-sm`} style={{ color: "#D97706" }} />
                <span className="text-sm font-medium" style={{ color: "rgba(30,20,5,0.65)" }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Brand Logos ── */}
      <section id="eb-brands" className="py-14 px-6" style={{ background: "#fffbf5", borderTop: "1px solid rgba(217,119,6,0.1)", borderBottom: "1px solid rgba(217,119,6,0.1)" }}>
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-semibold tracking-widest uppercase text-center mb-10" style={{ color: "rgba(30,20,5,0.4)", letterSpacing: "0.22em" }}>
            Engine Families We Supply
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {brands.map((brand) => (
              <div key={brand.name}
                className="flex flex-col items-center gap-3 p-5 rounded-2xl transition-all duration-200 cursor-default"
                style={{ background: "#ffffff", border: "1px solid rgba(217,119,6,0.12)" }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(217,119,6,0.4)";
                  (e.currentTarget as HTMLDivElement).style.background = "rgba(255,248,235,0.8)";
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.borderColor = "rgba(217,119,6,0.12)";
                  (e.currentTarget as HTMLDivElement).style.background = "#ffffff";
                }}
              >
                <div className="w-full flex items-center justify-center" style={{ height: "44px" }}>
                  <img
                    src={brand.logoUrl}
                    alt={`${brand.name} engine block supplier`}
                    className="max-h-full max-w-full object-contain"
                    style={{ filter: "grayscale(100%)", opacity: 0.7, maxHeight: "44px", maxWidth: "100px" }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLImageElement).style.filter = "grayscale(0%)"; (e.currentTarget as HTMLImageElement).style.opacity = "1"; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLImageElement).style.filter = "grayscale(100%)"; (e.currentTarget as HTMLImageElement).style.opacity = "0.7"; }}
                  />
                </div>
                <div className="text-center">
                  <p className="text-xs font-bold" style={{ color: "#1a1a1a" }}>{brand.name}</p>
                  <p className="text-xs font-light mt-0.5" style={{ color: "rgba(30,20,5,0.4)", fontSize: "10px" }}>{brand.models}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Engine Block Table ── */}
      <section id="eb-table" className="py-20 px-6" style={{ background: "#ffffff" }}>
        <div className="max-w-7xl mx-auto">
          {/* Section header */}
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
                style={{ background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.25)" }}>
                <i className="ri-table-line text-xs" style={{ color: "#D97706" }} />
                <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: "#D97706", letterSpacing: "0.2em" }}>
                  Global High-Demand Models
                </span>
              </div>
              <h2 className="font-black" style={{ fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)", color: "#1a1a1a" }}>
                Engine Block Specifications
              </h2>
              <p className="text-sm font-light mt-2" style={{ color: "rgba(30,20,5,0.5)", lineHeight: "1.8" }}>
                All blocks are available as bare / short block. Custom machining and OEM-spec tolerances on request.
              </p>
            </div>
            {/* Market filter */}
            <div className="flex items-center gap-2 flex-wrap">
              {["all", "Russia", "Middle East", "Europe"].map((m) => (
                <button key={m} onClick={() => setActiveMarket(m)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold tracking-wide transition-all duration-200 cursor-pointer whitespace-nowrap"
                  style={{
                    background: activeMarket === m ? "rgba(217,119,6,0.12)" : "rgba(30,20,5,0.04)",
                    border: activeMarket === m ? "1.5px solid rgba(217,119,6,0.45)" : "1px solid rgba(30,20,5,0.1)",
                    color: activeMarket === m ? "#D97706" : "rgba(30,20,5,0.5)",
                  }}
                >
                  {m === "all" ? "All Markets" : m}
                </button>
              ))}
            </div>
          </div>

          {/* Responsive table */}
          <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(217,119,6,0.18)" }}>
            {/* Desktop table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full" style={{ borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "rgba(255,248,235,0.9)", borderBottom: "1.5px solid rgba(217,119,6,0.2)" }}>
                    {["Engine Model", "OEM Part No.", "Application", "Material", "Bore × Stroke"].map((col) => (
                      <th key={col} className="text-left px-6 py-4 text-xs font-bold tracking-widest uppercase"
                        style={{ color: "rgba(30,20,5,0.5)", letterSpacing: "0.14em", whiteSpace: "nowrap" }}>
                        {col}
                      </th>
                    ))}
                    <th className="text-left px-6 py-4 text-xs font-bold tracking-widest uppercase"
                      style={{ color: "rgba(30,20,5,0.5)", letterSpacing: "0.14em" }}>
                      Markets
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((block, i) => (
                    <tr key={block.model}
                      style={{ borderBottom: i < filtered.length - 1 ? "1px solid rgba(217,119,6,0.1)" : "none", background: "transparent" }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = "rgba(255,248,235,0.6)"; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = "transparent"; }}
                    >
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <div>
                            <p className="font-black text-sm" style={{ color: "#1a1a1a" }}>{block.model}</p>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold mt-1"
                              style={{ background: `${block.badgeColor}15`, color: block.badgeColor, fontSize: "9px", letterSpacing: "0.1em" }}>
                              {block.badge}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <span className="font-mono text-xs font-semibold" style={{ color: "#D97706" }}>{block.oemPart}</span>
                      </td>
                      <td className="px-6 py-5">
                        <span className="text-xs font-light" style={{ color: "rgba(30,20,5,0.65)", lineHeight: "1.6" }}>{block.application}</span>
                      </td>
                      <td className="px-6 py-5">
                        <span className="text-xs font-medium" style={{ color: "rgba(30,20,5,0.7)" }}>{block.material}</span>
                      </td>
                      <td className="px-6 py-5">
                        <span className="font-mono text-sm font-bold" style={{ color: "#1a1a1a" }}>{block.bore}</span>
                        <p className="text-xs font-light mt-0.5" style={{ color: "rgba(30,20,5,0.45)" }}>{block.displacement} · {block.valvetrain}</p>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex flex-wrap gap-1.5">
                          {block.markets.map((m) => (
                            <span key={m} className="px-2 py-1 rounded-lg text-xs font-semibold"
                              style={{ background: marketColors[m]?.bg, color: marketColors[m]?.text, fontSize: "10px" }}>
                              {m}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="lg:hidden flex flex-col divide-y" style={{ borderColor: "rgba(217,119,6,0.1)" }}>
              {filtered.map((block) => (
                <div key={block.model} className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="font-black text-base" style={{ color: "#1a1a1a" }}>{block.model}</p>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold mt-1"
                        style={{ background: `${block.badgeColor}15`, color: block.badgeColor, fontSize: "9px" }}>
                        {block.badge}
                      </span>
                    </div>
                    <span className="font-mono text-xs font-semibold" style={{ color: "#D97706" }}>{block.oemPart}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p className="font-semibold mb-0.5" style={{ color: "rgba(30,20,5,0.4)", fontSize: "10px" }}>MATERIAL</p>
                      <p style={{ color: "rgba(30,20,5,0.7)" }}>{block.material}</p>
                    </div>
                    <div>
                      <p className="font-semibold mb-0.5" style={{ color: "rgba(30,20,5,0.4)", fontSize: "10px" }}>BORE × STROKE</p>
                      <p className="font-bold" style={{ color: "#1a1a1a" }}>{block.bore}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="font-semibold mb-0.5" style={{ color: "rgba(30,20,5,0.4)", fontSize: "10px" }}>APPLICATION</p>
                      <p style={{ color: "rgba(30,20,5,0.65)" }}>{block.application}</p>
                    </div>
                    <div className="col-span-2 flex flex-wrap gap-1.5 mt-1">
                      {block.markets.map((m) => (
                        <span key={m} className="px-2 py-1 rounded-lg text-xs font-semibold"
                          style={{ background: marketColors[m]?.bg, color: marketColors[m]?.text, fontSize: "10px" }}>
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="py-16 text-center">
                <i className="ri-search-line text-3xl mb-3 block" style={{ color: "rgba(217,119,6,0.3)" }} />
                <p className="text-sm font-light" style={{ color: "rgba(30,20,5,0.4)" }}>No models found for this market filter.</p>
              </div>
            )}
          </div>

          {/* Table footnote */}
          <p className="text-xs mt-4 text-center" style={{ color: "rgba(30,20,5,0.35)" }}>
            * All specifications are for reference. Final tolerances confirmed upon engineering review. Custom bore sizes available on request.
          </p>
        </div>
      </section>

      {/* ── Reverse Engineering CTA Banner ── */}
      <section className="py-16 px-6" style={{ background: "rgba(255,248,235,0.7)", borderTop: "1px solid rgba(217,119,6,0.12)", borderBottom: "1px solid rgba(217,119,6,0.12)" }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-6" style={{ background: "rgba(217,119,6,0.1)" }}>
            <i className="ri-scan-2-line text-2xl" style={{ color: "#D97706" }} />
          </div>
          <h2 className="font-black mb-4" style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)", color: "#1a1a1a" }}>
            Don&apos;t see your exact model?
          </h2>
          <p className="text-base font-light leading-relaxed mb-8 max-w-2xl mx-auto" style={{ color: "rgba(30,20,5,0.6)", lineHeight: "1.85" }}>
            Contact our engineering team for{" "}
            <strong style={{ color: "#1a1a1a" }}>Reverse Engineering solutions</strong>. Submit a worn
            block sample, OEM part number, or CAD file — we&apos;ll replicate it to 1:1 OEM specification
            without original blueprints. No model is too obscure.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <a href="#eb-cta"
              className="flex items-center gap-2 px-8 py-4 rounded-xl font-bold tracking-widest text-sm text-white transition-all hover:brightness-110 cursor-pointer whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", letterSpacing: "0.13em", textDecoration: "none" }}>
              <i className="ri-mail-send-line" /> Contact Engineering Team
            </a>
            <Link to="/foundry-machining"
              className="flex items-center gap-2 px-8 py-4 rounded-xl font-bold tracking-widest text-sm transition-all cursor-pointer whitespace-nowrap"
              style={{ background: "rgba(30,20,5,0.05)", border: "1.5px solid rgba(30,20,5,0.15)", color: "#1a1a1a", textDecoration: "none", letterSpacing: "0.13em" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(217,119,6,0.4)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(30,20,5,0.15)"; }}>
              <i className="ri-arrow-right-line" /> View Foundry Capabilities
            </Link>
          </div>
        </div>
      </section>

      {/* ── Quote Form ── */}
      <section id="eb-cta" className="py-20 px-6" style={{ background: "#ffffff" }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
                style={{ background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.25)" }}>
                <i className="ri-mail-send-line text-xs" style={{ color: "#D97706" }} />
                <span className="text-xs font-semibold tracking-widest uppercase" style={{ color: "#D97706", letterSpacing: "0.2em" }}>Request a Quote</span>
              </div>
              <h2 className="font-black mb-5" style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", color: "#1a1a1a", lineHeight: 1.15 }}>
                Ready to Source?
                <br /><span style={{ color: "#D97706" }}>Let&apos;s Talk.</span>
              </h2>
              <p className="text-base font-light leading-relaxed mb-8" style={{ color: "rgba(30,20,5,0.6)", lineHeight: "1.85" }}>
                Whether you need a standard catalog model or a fully custom reverse-engineered block,
                our team will provide a detailed quote within <strong style={{ color: "#1a1a1a" }}>24 business hours</strong>.
              </p>
              <div className="flex flex-col gap-4">
                {[
                  { icon: "ri-shield-check-line", title: "ISO 9001:2015 Certified", desc: "Every batch ships with full quality documentation" },
                  { icon: "ri-ship-line", title: "FCL & LCL Export", desc: "Guangzhou & Ningbo ports — Russia, ME, Europe" },
                  { icon: "ri-scan-2-line", title: "Reverse Engineering", desc: "No blueprints needed — sample-based replication" },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-xl shrink-0" style={{ background: "rgba(217,119,6,0.1)" }}>
                      <i className={`${item.icon} text-base`} style={{ color: "#D97706" }} />
                    </div>
                    <div>
                      <p className="text-sm font-bold" style={{ color: "#1a1a1a" }}>{item.title}</p>
                      <p className="text-xs font-light" style={{ color: "rgba(30,20,5,0.5)" }}>{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Right: form */}
            <div className="rounded-2xl p-8" style={{ background: "#fffbf5", border: "1px solid rgba(217,119,6,0.18)" }}>
              <InquiryForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-8 px-6" style={{ background: "rgba(255,243,220,0.5)", borderTop: "1px solid rgba(217,119,6,0.12)" }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs" style={{ color: "rgba(30,20,5,0.4)" }}>
            &copy; {new Date().getFullYear()} Chirunnexa — Engine Block Catalog | OEM Bare Block Supplier | Guangzhou Export Hub
          </p>
          <div className="flex items-center gap-6">
            <Link to="/" className="text-xs transition-colors duration-200 cursor-pointer" style={{ color: "rgba(30,20,5,0.4)", textDecoration: "none" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#D97706")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(30,20,5,0.4)")}>
              ← Main Site
            </Link>
            <Link to="/foundry-machining" className="text-xs transition-colors duration-200 cursor-pointer" style={{ color: "rgba(30,20,5,0.4)", textDecoration: "none" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#D97706")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(30,20,5,0.4)")}>
              Foundry &amp; Machining
            </Link>
            <a href="mailto:chirunnexa@gmail.com" className="text-xs transition-colors duration-200 cursor-pointer" style={{ color: "rgba(30,20,5,0.4)", textDecoration: "none" }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#D97706")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(30,20,5,0.4)")}>
              chirunnexa@gmail.com
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
